import { Routes } from '@common/constants';
import BasketContainer from '@features/Basket/containers/Basket';
import { BasketV2Connected } from '@features/Basket/v2';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { fetchBasketDetails } from '@store/basket/actions';
import { NormalizedStore } from '@store/entity/entity.types';
import { getSettings } from '@store/setting/actions';
import Cookie from 'js-cookie';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { useEffect } from 'react';
import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

type BasketProps = {
  language: string;
  propertyId: number;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const Basket: NextPage<BasketProps> = ({
  language,
  propertyId,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const isSinglePageCheckout = useFeatureIsOn('feature.single-page-checkout');
  const isCondensedCheckout = useFeatureIsOn('feature.condensed-checkout-v1');

  let checkoutRoute: string;
  if (isSinglePageCheckout) {
    checkoutRoute = Routes.CHECKOUT;
  } else if (isCondensedCheckout) {
    checkoutRoute = Routes.CHECKOUT;
  } else {
    checkoutRoute = Routes.CHECKOUT_SIGNIN;
  }

  useEffect(() => {
    if (isSinglePageCheckout || isCondensedCheckout) {
      Cookie.set('condensedCheckout', '1', { path: '/' });
    } else {
      Cookie.remove('condensedCheckout', { path: '/' });
    }
  }, [isSinglePageCheckout, isCondensedCheckout]);

  const pageName = t('app.basket');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      {isEcom2 ? (
        <BasketV2Connected
          language={language}
          checkoutRoute={checkoutRoute}
          isSinglePageCheckout={isSinglePageCheckout}
        />
      ) : (
        <BasketContainer
          language={language}
          propertyId={propertyId}
          checkoutRoute={checkoutRoute}
          isSinglePageCheckout={isSinglePageCheckout}
        />
      )}
    </>
  );
};

type BasketInitialProps = {
  property: Property;
  store: NormalizedStore;
  language: string;
  url: string;
} & NextPageContext;

Basket.getInitialProps = async ({ store, property, language, url }: BasketInitialProps) => {
  const basketId = Cookie.get('basketId') as unknown as number;
  await Promise.all([
    ...(basketId ? [fetchBasketDetails(property.id, basketId, language)(store.dispatch)] : []),
    getSettings()(store.dispatch),
  ]);
  return {
    url,
    language,
    propertyId: property.id,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default Basket;
